import { useQuery } from '@apollo/client';
import { CropOriginal } from '@mui/icons-material';
import { Box, Button, type DialogProps } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { ContentGrid, DialogRoot, MediaPreview } from '~/components/inputs/MediaSelect/components';
import { FolderContextProvider, useAppContext } from '~/contexts';
import type { ContentKind } from '~/generated/graphql';
import { MediaMultiSelectDialogDocument } from './MediaMultiSelectField.generated';

/* GraphQL */ `#graphql
query MediaMultiSelectDialog($networkId: Int!) {
  network(id: $networkId) {
    contentFolderTree
    contentFolders {
      canUpdate {
        ...AuthorizationResult
      }
      id
      name
    }
    id
  }
}
`;
export interface MediaMultiSelectDialogProps extends Omit<DialogProps, 'onChange'> {
  description?: string;
  excludeContentItemIds?: number[];
  hideUpload?: boolean;
  kind?: ContentKind[];
  onClose: () => void;
  onChange: (selectedIds: string[]) => void;
  /** Each click replaces the pick instead of adding to it, so at most one id comes back. */
  single?: boolean;
  title?: string;
  value?: string[];
}

export const MediaMultiSelectDialog = ({
  description,
  excludeContentItemIds,
  hideUpload,
  kind,
  onClose,
  onChange,
  single = false,
  title: titleProp,
  value,
  ...props
}: MediaMultiSelectDialogProps) => {
  const { t } = useTranslation();
  const title = titleProp ?? t('pages.content.dialogs.add_content_menu.add_content');
  const { currentNetwork } = useAppContext();

  const [selectedIds, setSelectedIds] = useState<string[]>(value ?? []);

  const { data } = useQuery(MediaMultiSelectDialogDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const handleItemClick = (id: string) => {
    if (single) return setSelectedIds([id]);
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const clearState = () => {
    setSelectedIds([]);
  };

  if (data?.network == null) return null;

  return (
    <FolderContextProvider
      folderTree={data.network.contentFolderTree}
      folders={data.network.contentFolders}
    >
      <Dialog
        PaperComponent={DialogRoot}
        TransitionProps={{ onExited: clearState }}
        fullWidth
        maxWidth="lg"
        onClose={onClose}
        {...props}
      >
        <DialogTitle onClose={onClose}>{title}</DialogTitle>

        <DialogContent sx={{ overflow: 'hidden' }}>
          <DialogContentTitle>
            {description ?? t('components.media_select.dialog.description.multi')}
          </DialogContentTitle>

          <ContentGrid
            excludeContentItemIds={excludeContentItemIds}
            hideUpload={hideUpload}
            kind={kind}
            onClick={handleItemClick}
            selectedIds={selectedIds}
            storageContext="media-select-multi"
            rememberLastFolder
          />
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={selectedIds.length === 0}
            onClick={() => {
              onClose();
              onChange(selectedIds);
            }}
          >
            {single
              ? t('common.actions.select')
              : t('common.dialogs.add_items_plural', { count: selectedIds.length })}
          </Button>
        </DialogActions>
      </Dialog>
    </FolderContextProvider>
  );
};

export interface MediaMultiSelectProps {
  disabled?: boolean;
  kind?: ContentKind[];
  onChange: (selectedIds: string[]) => void;
  title?: string;
  value: string[];
}

export const MediaMultiSelect = ({
  disabled,
  kind,
  onChange,
  title,
  value,
}: MediaMultiSelectProps) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <Box
      sx={{
        alignItems: 'flex-end',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {value.length > 0 && (
        <MediaPreview
          mediaItemIds={value.map((x) => Number(x.split('-').at(1)))}
          onRemove={(mediaItemId) =>
            onChange(value.filter((x) => x !== `MediaItem-${mediaItemId}`))
          }
          small
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {value.length > 0 && (
          <Button
            color="error"
            variant="contained"
            startIcon={<CropOriginal />}
            onClick={() => onChange([])}
            disabled={disabled}
            size="small"
          >
            {t('components.media_select.labels.remove_media')}
          </Button>
        )}

        <Button
          color="primary"
          variant="contained"
          startIcon={<CropOriginal />}
          onClick={() => setDialogOpen(true)}
          disabled={disabled}
          size="small"
        >
          {t('components.media_select.dialog.title.media')}
        </Button>
      </Box>

      {dialogOpen && (
        <MediaMultiSelectDialog
          kind={kind}
          onChange={(selectedIds) => onChange(selectedIds)}
          onClose={() => setDialogOpen(false)}
          open
          title={title}
          value={value}
        />
      )}
    </Box>
  );
};
